import React, { useState, useEffect } from 'react';
import { UserService } from '../../services/user.service';
import { IUserBase } from '../../types/type';
import { AutoparkService } from '../../services/autopark.service';
import { formatPhone } from '../../utils/phone';
import PhoneInput from '../PhoneInput/PhoneInput';
import './AddAutoparkModal.css';
import { fullName } from '../../utils/utils';

interface AddDriverModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  autoparkId: string;
}

const AddDriverModal: React.FC<AddDriverModalProps> = ({ isOpen, onClose, onSuccess, autoparkId }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [searchResults, setSearchResults] = useState<IUserBase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Очищаем состояние при открытии модального окна
      setPhoneNumber('');
      setIsPhoneValid(false);
      setSearchResults([]);
      setSelectedDriverId(null);
      setError(null);
      // Блокируем скролл
      document.body.style.overflow = 'hidden';
    } else {
      // Разблокируем скролл при закрытии
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSearch = async () => {
    if (!phoneNumber.trim() || !isPhoneValid) {
      setError('Введите корректный номер телефона');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Ищем водителя по полному номеру телефона
      // В автопарк можно добавить только зарегистрированного водителя
      const response = await UserService.getProfiles({
        role: 'driver',
        phone: phoneNumber,
        registered: true,
      });

      setSearchResults(response.data || []);

      if (response.data.length === 0) {
        setError('Водитель с таким номером телефона не найден');
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка при поиске водителя');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDriver = async () => {
    if (!selectedDriverId) {
      setError('Выберите водителя');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await AutoparkService.linkProfileToAutopark(autoparkId, selectedDriverId);
      onSuccess();
      onClose();
    } catch (err: any) {
      // Текст сервера, например 409 driver_not_registered «Водитель ещё не завершил регистрацию»
      setError(err.message || 'Ошибка при добавлении водителя');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePhoneChange = (numericValue: string, isValid: boolean) => {
    setPhoneNumber(numericValue);
    setIsPhoneValid(isValid);
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (searchResults.length === 0) {
        handleSearch();
      } else if (selectedDriverId) {
        handleAddDriver();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="autopark-modal-overlay" onClick={handleOverlayClick}>
      <button className="autopark-modal-close" onClick={onClose}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <g clipPath="url(#clip0_162_26)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12.0003 13.414L17.6573 19.071C17.8459 19.2532 18.0985 19.354 18.3607 19.3517C18.6229 19.3494 18.8737 19.2443 19.0591 19.0588C19.2445 18.8734 19.3497 18.6226 19.352 18.3604C19.3543 18.0982 19.2535 17.8456 19.0713 17.657L13.4143 12L19.0713 6.34303C19.2535 6.15443 19.3543 5.90182 19.352 5.63963C19.3497 5.37743 19.2445 5.12662 19.0591 4.94121C18.8737 4.7558 18.6229 4.65063 18.3607 4.64835C18.0985 4.64607 17.8459 4.74687 17.6573 4.92903L12.0003 10.586L6.34331 4.92903C6.15386 4.75137 5.90272 4.65439 5.64304 4.65861C5.38336 4.66283 5.1355 4.76791 4.95192 4.95162C4.76834 5.13533 4.66343 5.38326 4.6594 5.64295C4.65537 5.90263 4.75252 6.1537 4.93031 6.34303L10.5863 12L4.92931 17.657C4.8338 17.7493 4.75762 17.8596 4.70521 17.9816C4.6528 18.1036 4.62522 18.2348 4.62406 18.3676C4.62291 18.5004 4.64821 18.6321 4.69849 18.755C4.74877 18.8779 4.82302 18.9895 4.91692 19.0834C5.01081 19.1773 5.12246 19.2516 5.24536 19.3018C5.36825 19.3521 5.49993 19.3774 5.63271 19.3763C5.76549 19.3751 5.89671 19.3475 6.01872 19.2951C6.14072 19.2427 6.25106 19.1665 6.34331 19.071L12.0003 13.414Z"
              fill="#3563E9"
            />
          </g>
          <defs>
            <clipPath id="clip0_162_26">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </button>

      <div className="autopark-modal-content">
        <div className="autopark-modal-header">
          <h2 className="autopark-modal-title">Добавить водителя в автопарк</h2>
        </div>

        <div className="autopark-modal-body">
          {/* Поле поиска */}
          <div className="autopark-form-group">
            <label className="autopark-form-label">Поиск водителя по номеру телефона:</label>
            <div className="search-input-container">
              <PhoneInput
                id="driverPhoneSearch"
                value={phoneNumber}
                onChangeValue={handlePhoneChange}
                onKeyDown={handleKeyDown}
                placeholder="+7 (999) 999-99-99"
                disabled={loading}
                customInput={true}
              />
              <button
                type="button"
                onClick={handleSearch}
                className="search-button"
                disabled={loading || !phoneNumber.trim() || !isPhoneValid}
              >
                {loading ? 'Поиск...' : 'Найти'}
              </button>
            </div>
          </div>

          {/* Результаты поиска */}
          {searchResults.length > 0 && (
            <div className="search-results">
              <h3 className="search-results-title">Найденные водители:</h3>
              <div className="drivers-list">
                {searchResults.map((driver) => (
                  <div
                    key={driver.id}
                    className={`driver-item ${selectedDriverId === driver.id ? 'driver-item--selected' : ''}`}
                    onClick={() => setSelectedDriverId(driver.id)}
                  >
                    <div className="driver-info">
                      <div className="driver-name">{fullName(driver)}</div>
                      <div className="driver-phone">{formatPhone(driver.phone)}</div>
                    </div>
                    <div className="driver-selection">
                      <input
                        type="radio"
                        name="selectedDriver"
                        checked={selectedDriverId === driver.id}
                        onChange={() => setSelectedDriverId(driver.id)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Кнопки действий */}
          <div className="autopark-form-row" style={{ marginTop: '32px', justifyContent: 'flex-end', gap: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              className="autopark-form-button autopark-form-button--secondary"
              disabled={loading}
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={handleAddDriver}
              className="autopark-form-button autopark-form-button--primary"
              disabled={loading || !selectedDriverId}
            >
              {loading ? 'Добавление...' : 'Добавить водителя'}
            </button>
          </div>
        </div>

        {error && <div className="autopark-modal-error">{error}</div>}
      </div>
    </div>
  );
};

export default AddDriverModal;
